/**
 * Demo data for the School Analytics screens.
 *
 * This is a direct port of the dataset and derivations in the approved design
 * file ("Project Head Admin v1 Soft"). It lives in the page, not in the shared
 * components, so swapping it for API responses touches only this file.
 */

import { BandKey, Delta, Tone } from "shared/components/schoolAnalytics";

export interface School {
  id: number;
  name: string;
  campus: string;
  city: string;
  status:
    | "premium"
    | "expiring"
    | "repurchased"
    | "expired"
    | "overdue"
    | "demo"
    | "test";
  score: number;
  seats: number;
  enrolled: number;
  regular: number;
  teachersAssigning: number;
  teachers: number;
  campuses: number;
  contact: string;
  role: string;
  phone: string;
  levelUps: number;
}

type Row = [
  string, string, string, School["status"], number, number, number,
  number, number, number, number, string, string, string, number
];

const ROWS: Row[] = [
  ["Beaconhouse School System", "Gulberg Campus", "Lahore", "premium", 88, 320, 296, 41, 12, 14, 3, "Rabia Sheikh", "Head of Primary", "0300-4412876", 2],
  ["The City School", "Clifton Campus", "Karachi", "premium", 82, 280, 251, 36, 10, 11, 2, "Kamran Iqbal", "Campus Coordinator", "0321-2201938", 1],
  ["Lahore Grammar School", "DHA Phase 5", "Lahore", "expiring", 74, 240, 203, 22, 8, 12, 1, "Sana Malik", "Librarian", "0333-4577120", 2],
  ["Roots Millennium Schools", "Islamabad Flagship", "Islamabad", "premium", 91, 400, 382, 58, 17, 18, 4, "Ahmed Raza", "Principal", "0345-5109834", 2],
  ["Allied School", "Faisalabad Main", "Faisalabad", "expiring", 61, 180, 144, 12, 4, 9, 1, "Farhan Butt", "Section Head", "0301-7788459", 1],
  ["Bahria Town School", "Phase 8 Campus", "Rawalpindi", "premium", 79, 220, 190, 20, 7, 10, 2, "Mehwish Farooq", "Coordinator", "0312-5566010", 1],
  ["Froebel's International", "F-7 Campus", "Islamabad", "repurchased", 85, 160, 151, 25, 9, 9, 1, "Nadia Hussain", "Head Teacher", "0300-8556621", 1],
  ["Karachi Grammar School", "Kindergarten Section", "Karachi", "premium", 93, 200, 196, 34, 11, 11, 2, "Zohaib Ali", "Reading Lead", "0333-3129087", 1],
  ["Aitchison College", "Junior School", "Lahore", "premium", 87, 150, 141, 22, 8, 8, 1, "Imran Qureshi", "Head of Junior", "0321-4008811", 1],
  ["The Educators", "Multan Cantt", "Multan", "expiring", 44, 120, 71, 3, 0, 7, 1, "Asma Tariq", "Principal", "0300-6321156", 1],
  ["Dar-e-Arqam School", "Sialkot Campus", "Sialkot", "overdue", 38, 100, 52, 2, 0, 6, 1, "Bilal Ahmed", "Owner", "0345-6110925", 1],
  ["Model Lab School", "Narowal", "Narowal", "demo", 52, 60, 44, 4, 2, 4, 1, "Muhammad Sufyan", "Coordinator", "0301-4455780", 1],
  ["Ehsas Lab School", "Upgraded Plan", "Gujranwala", "expired", 29, 80, 31, 1, 0, 5, 1, "Head School", "Administrator", "0333-8001200", 1],
  ["Head School", "Model Town", "Lahore", "demo", 66, 40, 33, 5, 2, 3, 1, "Nabeela Akram", "Owner", "0300-9123344", 1],
  ["AlifLaila QA School", "Internal", "Lahore", "test", 72, 50, 41, 9, 3, 4, 1, "QA Team", "Tester", "0300-0000001", 2],
  ["Ehya Test Campus", "Internal", "Karachi", "test", 58, 30, 22, 3, 1, 2, 1, "Dev Team", "Tester", "0300-0000002", 1],
  ["Demo Sandbox School", "Internal", "Islamabad", "test", 35, 20, 9, 1, 0, 2, 1, "Sales Team", "Tester", "0300-0000003", 0],
];

export const ALL_SCHOOLS: School[] = ROWS.map((r, id) => ({
  id,
  name: r[0], campus: r[1], city: r[2], status: r[3], score: r[4],
  seats: r[5], enrolled: r[6], regular: r[7], teachersAssigning: r[8],
  teachers: r[9], campuses: r[10], contact: r[11], role: r[12],
  phone: r[13], levelUps: r[14],
}));

/** Test accounts are excluded from every headline figure, as in the design. */
export const LIVE_SCHOOLS = ALL_SCHOOLS.filter((s) => s.status !== "test");

export const BAND_STYLE: Record<BandKey, { bg: string; color: string; def: string }> = {
  Healthy: { bg: "#ecfdf3", color: "#027a48", def: "Score 80+, read in last 7 days" },
  Slipping: { bg: "#fffaeb", color: "#b54708", def: "Score 50–79, or quiet 8–21 days" },
  Dead: { bg: "#fef3f2", color: "#b42318", def: "Below 50, or quiet 21+ days" },
};

export const bandOf = (school: School): BandKey =>
  school.score >= 80 ? "Healthy" : school.score >= 50 ? "Slipping" : "Dead";

export const fmt = (n: number): string => n.toLocaleString("en-US");

/** Deterministic pseudo-random, so demo figures stay stable across renders. */
export const seed = (n: number): number => {
  const x = Math.sin(n * 9301 + 49297) * 233280;
  return x - Math.floor(x);
};

export const readingMinutes = (school: School, days: number): number =>
  Math.round(school.enrolled * (school.score / 100) * 38 * (days / 30));

export const previousMinutes = (school: School, days: number): number =>
  Math.round(readingMinutes(school, days) * (0.78 + seed(school.id) * 0.4));

/** Builds the delta chip shown beside a figure. */
export const makeDelta = (
  now: number,
  prev: number,
  against?: string,
  /** Dead/overdue style figures improve when they fall. */
  invert = false
): Delta => {
  const pct = prev ? ((now - prev) / prev) * 100 : 0;
  const up = now >= prev;
  const good = invert ? !up : up;
  const tone: Tone = good ? "success" : "danger";
  return { value: `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`, tone, against };
};

export const countDelta = (diff: number, invert = false): Delta => ({
  value: `${diff >= 0 ? "+" : ""}${diff}`,
  tone: (invert ? diff <= 0 : diff >= 0) ? "success" : "danger",
});

export const CHART_COLORS = [
  "#9a469b", "#ef437b", "#f4b740", "#2e9e8f", "#3b82f6", "#8b5cf6",
];

export const FUNNEL_COLORS = ["#7b2c7c", "#9a469b", "#c15fbe", "#ef437b"];

const fmtDate = (d: Date) =>
  d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

/** Y-m-d in local time, the format every school-stat endpoint expects. */
const isoDate = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** A custom range is only usable once the user has picked both ends. */
export const isRangeReady = (
  preset: string | number,
  custom: { from: string; to: string }
): boolean => preset !== "custom" || Boolean(custom.from && custom.to);

/** Resolves the preset (or custom pair) into labels and a day count. */
export function resolveRange(
  preset: string | number,
  custom: { from: string; to: string }
) {
  // A half-picked custom range has no dates to send or label yet.
  if (!isRangeReady(preset, custom)) {
    return {
      days: 0,
      from: "",
      to: "",
      rangeLabel: "Pick a start and an end date",
      prevLabel: "",
      prevShort: "previous period",
    };
  }

  const days =
    preset === "custom"
      ? Math.max(
          1,
          Math.round(
            (new Date(custom.to).getTime() - new Date(custom.from).getTime()) /
              864e5
          ) + 1
        )
      : preset === "term"
      ? 70
      : Number(preset);

  const end = preset === "custom" ? new Date(custom.to) : new Date();
  const start =
    preset === "custom"
      ? new Date(custom.from)
      : new Date(end.getTime() - (days - 1) * 864e5);
  const prevStart = new Date(start.getTime() - days * 864e5);
  const prevEnd = new Date(start.getTime() - 864e5);

  return {
    days,
    /** Sent to the API as `from`/`to`. */
    from: isoDate(start),
    to: isoDate(end),
    rangeLabel:
      preset === "term"
        ? `This term · ${fmtDate(start)} – ${fmtDate(end)}`
        : `${fmtDate(start)} – ${fmtDate(end)} (${days} days)`,
    prevLabel: `${fmtDate(prevStart)} – ${fmtDate(prevEnd)}`,
    prevShort: preset === "term" ? "last term" : `previous ${days} days`,
  };
}

/* ------------------------------------------------------------------ *
 * Block-level builders                                                *
 * ------------------------------------------------------------------ */

/** Seats bought → children reading, as four funnel stages. */
export function buildFunnel(prevShort: string) {
  const seats = LIVE_SCHOOLS.reduce((sum, s) => sum + s.seats, 0);
  const created = LIVE_SCHOOLS.reduce((sum, s) => sum + s.enrolled, 0);
  const logged = Math.round(created * 0.83);
  const three = Math.round(created * 0.41);

  const raw: [string, string, number, number][] = [
    ["seats", "Seats purchased", seats, seats],
    ["created", "Student accounts created", created, Math.round(created * 0.95)],
    ["logged", "Read at least once", logged, Math.round(logged * 0.91)],
    ["three", "Finished 3+ books last month", three, Math.round(three * 1.06)],
  ];

  return raw.map(([key, label, value, prev], index) => ({
    key,
    stage: index + 1,
    label,
    value,
    share: (value / seats) * 100,
    color: FUNNEL_COLORS[index],
    delta: makeDelta(value, prev, prevShort),
    drop:
      index === 0
        ? undefined
        : ((raw[index - 1][2] - value) / raw[index - 1][2]) * 100,
  }));
}

export const MIX_DATA: Record<string, [string, number][]> = {
  genre: [["Adventure", 24], ["Folk tales", 19], ["Science", 15], ["Animals", 13], ["Poetry", 9], ["History", 7]],
  language: [["Urdu", 56], ["English", 41], ["Sindhi", 3]],
  age: [["4–7 years", 41], ["8–11 years", 36], ["12–14 years", 16], ["15 & above", 7]],
  type: [["Picture book", 44], ["Chapter book", 31], ["Audio book", 17], ["Comic", 8]],
  source: [["Teacher assigned", 52], ["Self picked", 33], ["Recommended", 15]],
  publisher: [["Oxford Pakistan", 22], ["Ferozsons", 18], ["MU Publishers", 14], ["Walker Books", 12], ["Test House", 9]],
};

export const MIX_DIMENSIONS = [
  { key: "genre", label: "Genre" },
  { key: "language", label: "Language" },
  { key: "age", label: "Age group" },
  { key: "type", label: "Book type" },
  { key: "source", label: "Source" },
  { key: "publisher", label: "Publisher" },
];

export function buildMix(dimension: string) {
  return (MIX_DATA[dimension] ?? []).map(([label, share], index) => {
    const drift = (seed(index + share) - 0.45) * 8;
    return {
      label,
      share,
      color: CHART_COLORS[index % CHART_COLORS.length],
      delta: {
        value: `${drift >= 0 ? "+" : ""}${drift.toFixed(1)} pts`,
        tone: (drift >= 0 ? "success" : "danger") as Tone,
      },
    };
  });
}

const BOOK_ROWS: [string, string, string, string, string, number, number, string][] = [
  ["Chuha aur Sher", "Amjad Islam", "Ferozsons", "Urdu", "4–7", 1420, 0.62, "#7b2c7c"],
  ["The Brave Little Kite", "Sana Munir", "Oxford Pakistan", "English", "4–7", 1285, 0.58, "#9a469b"],
  ["Neeli Chhatri", "Zehra Nigah", "MU Publishers", "Urdu", "8–11", 1140, 0.49, "#b86bb8"],
  ["Where Is Bilal?", "Rumana Husain", "Walker Books", "English", "4–7", 980, 0.71, "#ef437b"],
  ["Dadi Ki Kahaniyan", "Fatima Surayya", "Ferozsons", "Urdu", "8–11", 905, 0.44, "#c15fbe"],
  ["Sindhi Lok Kahani", "Amar Jaleel", "Sindhi Adabi", "Sindhi", "12–14", 640, 0.39, "#d391d3"],
];

export const BOOK_SORTS = [
  { key: "reads", label: "By reads" },
  { key: "finished", label: "By finishes" },
];

export function buildBooks(sort: string) {
  return BOOK_ROWS.map((row, index) => {
    const reads = row[5];
    const finished = Math.round(reads * row[6]);
    const drift = (seed(index + 40) - 0.4) * 30;

    return {
      id: row[0],
      title: row[0],
      author: row[1],
      language: row[3],
      age: row[4],
      cover: row[7],
      initials: row[0].split(" ").map((w) => w[0]).slice(0, 2).join(""),
      readsN: reads,
      finishedN: finished,
      reads: fmt(reads),
      finished: fmt(finished),
      finishPct: `${Math.round((finished / reads) * 100)}%`,
      delta: {
        value: `${drift >= 0 ? "+" : ""}${drift.toFixed(1)}%`,
        tone: (drift >= 0 ? "success" : "danger") as Tone,
      },
    };
  })
    .sort((a, b) =>
      sort === "reads" ? b.readsN - a.readsN : b.finishedN - a.finishedN
    )
    .map((book, index) => ({ ...book, rank: index + 1 }));
}

export const TREND_METRICS = [
  { key: "books", label: "Books Read" },
  { key: "cumulative", label: "Cumulative Reading" },
  { key: "engagement", label: "Engagement" },
];

const TREND_DATA: Record<string, { points: number[]; color: string; suffix?: string }> = {
  books: { points: [6, 8, 3, 2, 9, 11, 10], color: "#9a469b" },
  cumulative: { points: [6, 14, 17, 19, 28, 39, 49], color: "#ef437b" },
  engagement: { points: [38, 44, 30, 27, 47, 52, 50], color: "#2e9e8f", suffix: "%" },
};

export const TREND_LABELS = [
  "12 Aug – 18 Aug",
  "19 Aug – 25 Aug",
  "26 Aug – 01 Sep",
  "02 Sep – 08 Sep",
  "09 Sep – 10 Sep",
];

/** Resamples a fixed series onto however many x labels the grain produces. */
export function buildTrend(metric: string) {
  const def = TREND_DATA[metric] ?? TREND_DATA.books;
  const label = TREND_METRICS.find((m) => m.key === metric)?.label ?? "";
  const n = TREND_LABELS.length;

  const points = Array.from({ length: n }, (_, i) =>
    def.points[Math.round((i * (def.points.length - 1)) / Math.max(1, n - 1))]
  );

  const total = points.reduce((sum, v) => sum + v, 0);
  const prevTotal = total * (0.85 + seed(11) * 0.3);

  return {
    series: [{ key: metric, label, color: def.color, points, suffix: def.suffix }],
    legend: [
      {
        key: metric,
        label,
        color: def.color,
        total: def.suffix === "%" ? `${Math.round(total / n)}%` : fmt(total),
        delta: makeDelta(total, prevTotal),
      },
    ],
  };
}

export const ACCOUNT_FILTERS = [
  { key: "all", label: "All accounts" },
  { key: "demo", label: "Demo" },
  { key: "premium", label: "Premium" },
  { key: "test", label: "Test" },
  { key: "expired", label: "Expired" },
];

export const BAND_FILTERS = [
  { key: "All", label: "All" },
  { key: "Healthy", label: "Healthy" },
  { key: "Slipping", label: "Slipping" },
  { key: "Dead", label: "Dead" },
];

/** Days until renewal, derived from account status as in the design file. */
export function leadTime(school: School): number {
  const table: Record<School["status"], number[]> = {
    expiring: [9, 14, 26],
    overdue: [-4],
    expired: [-12],
    demo: [21, 30],
    premium: [120, 150, 180, 210, 240, 300],
    repurchased: [365],
    test: [999],
  };
  const options = table[school.status];
  return options[school.id % options.length];
}

/** Filters, ranks and formats every school for the "Reading by school" table. */
export function buildRanking(
  accountFilter: string,
  bandFilter: string,
  days: number
) {
  const matchesAccount = (school: School) => {
    if (accountFilter === "all") return true;
    if (accountFilter === "premium") {
      return ["premium", "repurchased", "expiring"].includes(school.status);
    }
    if (accountFilter === "expired") {
      return ["expired", "overdue"].includes(school.status);
    }
    return school.status === accountFilter;
  };

  const pool =
    accountFilter === "test"
      ? ALL_SCHOOLS.filter((s) => s.status === "test")
      : LIVE_SCHOOLS;

  return pool
    .filter(
      (school) =>
        (bandFilter === "All" || bandOf(school) === bandFilter) &&
        matchesAccount(school)
    )
    .map((school) => ({
      school,
      minutes: readingMinutes(school, days),
      previous: previousMinutes(school, days),
    }))
    .sort((a, b) => b.minutes - a.minutes)
    .map(({ school, minutes, previous }, index) => ({
      id: school.id,
      rank: index + 1,
      name: school.name,
      campus: school.campus,
      city: school.city,
      band: bandOf(school),
      minutes: `${fmt(minutes)} min`,
      delta: makeDelta(minutes, previous),
      regular: school.regular,
      regularPct: `${Math.round((school.regular / school.enrolled) * 100)}%`,
      seatsUsed: school.enrolled,
      seats: school.seats,
      lead: leadTime(school),
      contact: school.contact,
      role: school.role,
      phone: school.phone,
      initials: school.contact.split(" ").map((w) => w[0]).slice(0, 2).join(""),
    }));
}

/* ------------------------------------------------------------------ *
 * School detail                                                       *
 * ------------------------------------------------------------------ */

const STATUS_LABEL: Record<School["status"], string> = {
  premium: "Active",
  repurchased: "Active",
  expiring: "Active",
  demo: "Trial",
  expired: "Expired",
  overdue: "Overdue",
  test: "Test",
};

export function findSchool(id: number): School | undefined {
  return ALL_SCHOOLS.find((school) => school.id === id);
}

/** Everything the detail screen needs for one school, in one pass. */
export function buildSchoolDetail(school: School) {
  const price = school.seats * 1696;
  const discounted = Math.round(price * 0.745);
  const paysInInstalments = school.id % 2 === 0;

  const start = new Date("2026-04-21");
  const end = new Date("2026-10-18");
  const today = new Date("2026-09-10");

  const daysLeft = Math.max(
    0,
    Math.round((end.getTime() - today.getTime()) / 864e5)
  );
  const progress = Math.min(
    100,
    Math.round(
      ((today.getTime() - start.getTime()) / (end.getTime() - start.getTime())) *
        100
    )
  );

  const planName = `${school.name} ${
    school.status === "repurchased" || school.status === "premium"
      ? "Upgraded "
      : ""
  }Plan`;

  const plan = {
    name: planName,
    status: STATUS_LABEL[school.status],
    progress,
    daysUsed: 180 - daysLeft,
    daysLeft,
    paid: fmt(
      paysInInstalments ? Math.round((discounted * 2) / 3) : discounted
    ),
    total: fmt(discounted),
    facts: [
      { label: "Start date", value: "21 Apr 2026" },
      { label: "Validity", value: "18 Oct 2026" },
      { label: "Duration", value: "180 days" },
      {
        label: "Plan type",
        value: paysInInstalments ? "Instalments (3)" : "Full payment",
      },
      { label: "No. of students", value: fmt(school.seats) },
      { label: "No. of teachers", value: school.teachers },
      { label: "Books", value: fmt(1662) },
    ],
  };

  const third = Math.round(discounted / 3);
  const payments = paysInInstalments
    ? [
        {
          id: `TXN-${48120 + school.id}`,
          amount: fmt(third),
          method: "Bank transfer",
          instalment: "1 of 3",
          status: "Approved",
          due: "21 Apr 2026",
          purchased: "21 Apr 2026",
        },
        {
          id: `TXN-${48311 + school.id}`,
          amount: fmt(third),
          method: "Manual",
          instalment: "2 of 3",
          status: "Approved",
          due: "21 Jun 2026",
          purchased: "19 Jun 2026",
        },
        {
          id: "—",
          amount: fmt(third),
          method: "—",
          instalment: "3 of 3",
          status: school.status === "overdue" ? "Due" : "Pending",
          due: "21 Aug 2026",
          purchased: "—",
        },
      ]
    : [
        {
          id: `TXN-${48120 + school.id}`,
          amount: fmt(discounted),
          method: "Manual",
          instalment: "—",
          status: "Approved",
          due: "21 Apr 2026",
          purchased: "21 Apr 2026",
        },
        {
          id: `TXN-${41210 + school.id}`,
          amount: fmt(price),
          method: "Manual",
          instalment: "—",
          status: "Rejected",
          due: "16 Dec 2025",
          purchased: "16 Dec 2025",
          flagged: true,
          flagNote: "Receipt could not be verified",
        },
        {
          id: `TXN-${39877 + school.id}`,
          amount: fmt(price),
          method: "Bank transfer",
          instalment: "—",
          status: "Approved",
          due: "20 Jul 2025",
          purchased: "20 Jul 2025",
        },
      ];

  const planHistory = [
    {
      id: "current",
      name: planName,
      duration: "180 Days",
      price: fmt(price),
      discounted: "Rs. 0",
      status: "Active",
      start: "21 Apr 2026",
      end: "18 Oct 2026",
    },
    {
      id: "superseded",
      name: planName,
      duration: "180 Days",
      price: fmt(price),
      discounted: `Rs. ${fmt(discounted)}`,
      discountTone: "saved" as const,
      status: "Inactive",
      start: "—",
      end: "—",
    },
    {
      id: "previous",
      name: `${school.name} Plan`,
      duration: "180 Days",
      price: fmt(price),
      discounted: "—",
      discountTone: "none" as const,
      status: "Expired",
      start: "20 Jul 2025",
      end: "16 Jan 2026",
    },
  ];

  const planCompare = [
    { label: "Duration", previous: "180 Days", next: "180 Days" },
    {
      label: "Student count",
      previous: fmt(school.seats),
      next: fmt(school.seats),
    },
    {
      label: "Teacher count",
      previous: school.teachers,
      next: school.teachers,
    },
    { label: "Books count", previous: "60", next: "115", changed: true },
  ];

  return { plan, payments, planHistory, planCompare };
}
